import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import { PaymentIssue } from '../../../../types/admin';
import { format } from 'date-fns';
import { formatCurrency } from '../../../../utils/formatters';

interface PaymentIssueDetailModalProps {
  open: boolean;
  issue: PaymentIssue;
  onClose: () => void;
  onRetryPayment?: (issueId: string) => Promise<{ success: boolean }>;
}

export default function PaymentIssueDetailModal({
  open,
  issue,
  onClose,
  onRetryPayment,
}: PaymentIssueDetailModalProps) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    if (onRetryPayment) {
      setRetrying(true);
      try {
        await onRetryPayment(issue.id);
        onClose();
      } finally {
        setRetrying(false);
      }
    }
  };

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    </Grid>
  );

  const issueTypeColor = {
    failed_payment: 'error' as const,
    expired_card: 'warning' as const,
    declined: 'error' as const,
    chargeback: 'error' as const,
    dispute: 'warning' as const,
  };

  const statusColor = {
    new: 'error' as const,
    retrying: 'warning' as const,
    resolved: 'success' as const,
    given_up: 'default' as const,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Payment Issue Details
        <Typography variant="body2" color="text.secondary">
          {issue.id}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {issue.status === 'new' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            This payment issue requires attention. Consider retrying the payment or contacting the customer.
          </Alert>
        )}

        {issue.status === 'retrying' && issue.next_retry_at && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Payment retry scheduled for {format(new Date(issue.next_retry_at), 'PPP p')}
          </Alert>
        )}

        {issue.status === 'resolved' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            This payment issue was resolved on {issue.resolved_at && format(new Date(issue.resolved_at), 'PPP')}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Customer Information
          </Typography>
          <DetailRow label="Name" value={issue.user_name} />
          <DetailRow label="Email" value={issue.user_email} />
          <DetailRow label="User ID" value={issue.user_id} />
          <DetailRow label="Subscription ID" value={issue.subscription_id} />
          <DetailRow label="Paddle Subscription ID" value={issue.paddle_subscription_id} />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Issue Details
          </Typography>
          <DetailRow
            label="Issue Type"
            value={
              <Chip
                label={issue.issue_type.replace('_', ' ').toUpperCase()}
                size="small"
                color={issueTypeColor[issue.issue_type] || 'default'}
              />
            }
          />
          <DetailRow
            label="Status"
            value={<Chip label={issue.status.toUpperCase()} size="small" color={statusColor[issue.status]} />}
          />
          <DetailRow label="Amount" value={formatCurrency(issue.amount)} />
          <DetailRow label="Currency" value={issue.currency} />
          <DetailRow label="Error Message" value={issue.error_message} />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Retry Information
          </Typography>
          <DetailRow label="Retry Count" value={`${issue.retry_count} / ${issue.max_retries}`} />
          {issue.last_retry_at && (
            <DetailRow label="Last Retry" value={format(new Date(issue.last_retry_at), 'PPP p')} />
          )}
          {issue.next_retry_at && (
            <DetailRow label="Next Retry" value={format(new Date(issue.next_retry_at), 'PPP p')} />
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Dates
          </Typography>
          <DetailRow label="Issue Date" value={format(new Date(issue.issue_date), 'PPP p')} />
          <DetailRow label="Created" value={format(new Date(issue.created_at), 'PPP p')} />
          <DetailRow label="Last Updated" value={format(new Date(issue.updated_at), 'PPP p')} />
          {issue.resolved_at && (
            <DetailRow label="Resolved" value={format(new Date(issue.resolved_at), 'PPP p')} />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {issue.status !== 'resolved' && issue.status !== 'given_up' && (
          <Button onClick={handleRetry} color="warning" variant="contained" disabled={retrying}>
            {retrying ? 'Retrying...' : 'Retry Payment Now'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
